import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getGratitudeEntries, deleteGratitudeEntry, type GratitudeEntry } from '../utils/localStorage'
import BurningPaper from './BurningPaper'
import { useTheme } from './ThemeProvider'

const JournalEntries = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [entries, setEntries] = useState<GratitudeEntry[]>([])
  const [burningEntry, setBurningEntry] = useState<GratitudeEntry | null>(null)

  useEffect(() => {
    const loadedEntries = getGratitudeEntries()
    setEntries(loadedEntries)
  }, [])

  const handleBurnEntry = (entry: GratitudeEntry) => {
    setBurningEntry(entry)
  }

  const completeBurn = () => {
    if (burningEntry) {
      deleteGratitudeEntry(burningEntry.id)
      const updatedEntries = getGratitudeEntries()
      setEntries(updatedEntries)
    }
    setBurningEntry(null)
  }

  const cancelBurn = () => {
    setBurningEntry(null)
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const groupEntriesByDate = (entries: GratitudeEntry[]) => {
    const groups: { [key: string]: GratitudeEntry[] } = {}
    
    entries.forEach(entry => {
      const dateKey = entry.date
      if (!groups[dateKey]) {
        groups[dateKey] = []
      }
      groups[dateKey].push(entry)
    })
    
    return groups
  }

  const groupedEntries = groupEntriesByDate(entries)
  const dateKeys = Object.keys(groupedEntries).sort((a, b) => {
    const dateA = new Date(groupedEntries[a][0].timestamp)
    const dateB = new Date(groupedEntries[b][0].timestamp)
    return dateB.getTime() - dateA.getTime()
  })

  if (entries.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-cream-25 dark:bg-sage-900">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-cream-50/80 dark:bg-sage-800/80 border-b border-cream-100/50 dark:border-sage-700/50">
          <button
            onClick={() => navigate('/journal')}
            className="p-3 sm:p-4 rounded-2xl hover:bg-cream-100 dark:hover:bg-sage-700 transition-colors focus:outline-none focus:ring-4 focus:ring-cream-100 dark:focus:ring-sage-700"
          >
            <svg className="w-5 h-5 text-cream-500 dark:text-cream-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-base sm:text-lg font-light text-cream-600 dark:text-cream-300">Your Journal</h1>
          <div className="w-11 sm:w-12"></div>
        </div>

        {/* Empty State */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl mb-6">📖</div>
            <h2 className="text-cream-600 dark:text-cream-300 font-light text-lg sm:text-xl mb-4">
              Your journal is waiting
            </h2>
            <p className="text-cream-500 dark:text-cream-400 font-light text-sm sm:text-base leading-relaxed max-w-xs mx-auto mb-8">
              Start writing your first gratitude entry to see it appear here.
            </p>
            <button
              onClick={() => navigate('/journal')}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-cream-100/80 hover:bg-cream-200/60 
                         dark:bg-cream-900/30 dark:hover:bg-cream-800/40
                         text-cream-600 dark:text-cream-300 rounded-2xl text-sm sm:text-base font-light 
                         border border-cream-200/50 dark:border-cream-700/40 
                         hover:border-cream-300/60 dark:hover:border-cream-600/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-cream-100 dark:focus:ring-cream-700/30"
            >
              Write your first entry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-cream-25 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-cream-50/80 dark:bg-sage-800/80 border-b border-cream-100/50 dark:border-sage-700/50">
        <button
          onClick={() => navigate('/journal')}
          className="p-3 sm:p-4 rounded-2xl hover:bg-cream-100 dark:hover:bg-sage-700 transition-colors focus:outline-none focus:ring-4 focus:ring-cream-100 dark:focus:ring-sage-700"
        >
          <svg className="w-5 h-5 text-cream-500 dark:text-cream-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-base sm:text-lg font-light text-cream-600 dark:text-cream-300">Your Journal</h1>
        <button
          onClick={() => navigate('/tools')}
          className="p-3 sm:p-4 rounded-2xl hover:bg-cream-100 dark:hover:bg-sage-700 transition-colors focus:outline-none focus:ring-4 focus:ring-cream-100 dark:focus:ring-sage-700"
        >
          <svg className="w-5 h-5 text-cream-500 dark:text-cream-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Journal Content */}
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 overflow-hidden">
        <div className="max-w-2xl mx-auto">
          {/* Journal Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="text-xl sm:text-2xl mb-4">📖</div>
            <h2 className="text-cream-600 dark:text-cream-300 font-light text-lg sm:text-xl mb-2">
              Your Gratitude Journal
            </h2>
            <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm">
              {entries.length} {entries.length === 1 ? 'entry' : 'entries'} saved
            </p>
          </div>

          {/* Journal Pages */}
          <div className="space-y-8 sm:space-y-12">
            {dateKeys.map((dateKey) => (
              <div key={dateKey} className="bg-white/60 dark:bg-sage-800/60 rounded-3xl border border-cream-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm overflow-hidden">
                {/* Date Header */}
                <div className="border-b border-cream-100/50 dark:border-sage-700/50 pb-4 sm:pb-6 mb-6 sm:mb-8">
                  <h3 className="text-cream-600 dark:text-cream-300 font-light text-base sm:text-lg text-center">
                    {dateKey}
                  </h3>
                </div>

                {/* Entries for this date */}
                <div className="space-y-6 sm:space-y-8">
                  {groupedEntries[dateKey]
                    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
                    .map((entry, index) => (
                    <div key={entry.id} className="relative">
                      {/* Entry */}
                      <div className="bg-cream-50/60 dark:bg-sage-700/60 rounded-2xl p-4 sm:p-6 border border-cream-100/30 dark:border-sage-600/30 overflow-hidden">
                        <div className="flex items-start justify-between mb-3 sm:mb-4 gap-3 sm:gap-4">
                          <p className="text-cream-500 dark:text-cream-400 font-light text-xs italic flex-1 min-w-0 break-words">
                            "{entry.prompt}"
                          </p>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <span className="text-cream-400 dark:text-cream-500 text-xs font-light whitespace-nowrap">
                              {formatTime(entry.timestamp)}
                            </span>
                            <button
                              onClick={() => handleBurnEntry(entry)}
                              className="p-1 rounded-lg hover:bg-peach-100/60 dark:hover:bg-peach-900/60 transition-colors focus:outline-none focus:ring-2 focus:ring-peach-100 dark:focus:ring-peach-700 flex-shrink-0 group"
                              title="Burn this entry"
                            >
                              <svg className="w-3 h-3 text-cream-400 dark:text-cream-500 group-hover:text-peach-500 dark:group-hover:text-peach-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 1-4 4-4 2.5 0 4 1.5 4 4 0 .5 0 1 .5 1.5a3 3 0 002.5 2.5c-.5 1.5-2 3-5.5 3z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="overflow-hidden">
                          <p className="text-cream-600 dark:text-cream-300 font-light leading-relaxed text-xs sm:text-sm break-words overflow-wrap-anywhere hyphens-auto">
                            {entry.content}
                          </p>
                        </div>
                      </div>

                      {/* Entry separator (except for last entry) */}
                      {index < groupedEntries[dateKey].length - 1 && (
                        <div className="flex justify-center my-4 sm:my-6">
                          <div className="w-8 h-px bg-cream-200/50 dark:bg-sage-600/50"></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Add New Entry Button */}
          <div className="text-center mt-8 sm:mt-12">
            <button
              onClick={() => navigate('/journal')}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-cream-100/80 hover:bg-cream-200/60 
                         dark:bg-cream-900/30 dark:hover:bg-cream-800/40
                         text-cream-600 dark:text-cream-300 rounded-2xl text-sm sm:text-base font-light 
                         border border-cream-200/50 dark:border-cream-700/40 
                         hover:border-cream-300/60 dark:hover:border-cream-600/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-cream-100 dark:focus:ring-cream-700/30"
            >
              Add new entry
            </button>
          </div>

          {/* Return to Tools Button */}
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/tools')}
              className="text-cream-400 dark:text-cream-500 text-xs sm:text-sm font-light hover:text-cream-500 dark:hover:text-cream-400 transition-colors"
            >
              Return to tools
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-12 sm:mt-16">
            <p className="text-cream-400 dark:text-cream-500 text-xs sm:text-sm font-light leading-relaxed max-w-xs mx-auto">
              Your entries are saved securely in your browser. They stay private to you.
            </p>
          </div>
        </div>
      </div>

      {/* Burning Paper Animation */}
      {burningEntry && (
        <BurningPaper
          text={burningEntry.content}
          onComplete={completeBurn}
          onCancel={cancelBurn}
        />
      )}
    </div>
  )
}

export default JournalEntries